import { PaymentConfirmType } from '@did/das-balance-types'
import { CheckBox, DasButton, Iconfont } from '@did/uikit'
import React, { useEffect, useMemo } from 'react'
import { DOGE, USD, CoinType, DASBalanceTokenId } from '@did/constants/chain'
import { useDasBalanceContext } from '@did/das-app-context'
import { isMobile, isTokenPocket, thousandSplit, cn } from '@did/tools'
import { ITokenInfo } from '@did/types'

const PaymentErrorTips = ({
  selectedToken
}: {
  selectedToken?: ITokenInfo
}) => {
  const { tt } = useDasBalanceContext()

  const errorToken = useMemo(() => {
    if (selectedToken?.coin_type === CoinType.doge && !isTokenPocket()) {
      return DOGE.symbol
    }
    return null
  }, [selectedToken])

  if (!errorToken) return <></>

  return (
    <div className=" my-2 text-sm text-red-700 text-center font-semibold">
      {tt('The wallet environment does not support {token} payments', {
        token: `${errorToken}`
      })}
    </div>
  )
}

export const PaymentConfirm: PaymentConfirmType = ({
  tokenList,
  selectedToken,
  onTokenChange,
  fiatTermsLink,
  stripeServiceFee,
  paidTokenAmount,
  stripePaidAmount,
  onPay,
  showAll,
  payLoading
}) => {
  const { tt, lang, outSiteUrl, connectedAccount } = useDasBalanceContext()
  const [isAgreeFiatTerms, setIsAgreeFiatTerms] = React.useState(false)

  const payDisabled = useMemo(() => {
    if (!selectedToken) return true
    return (
      (selectedToken.coin_type === CoinType.doge && !isTokenPocket()) ||
      (selectedToken.token_id === USD.tokenId && !isAgreeFiatTerms)
    )
  }, [selectedToken, isAgreeFiatTerms])

  const ckbWalletOptions = () => {
    const options = tokenList.filter((option: ITokenInfo) => {
      return option.token_id === DASBalanceTokenId
    })

    if (options) {
      return options
    }
    return []
  }

  const stripeOptions = () => {
    const options = tokenList.filter((option: ITokenInfo) => {
      return option.token_id === USD.tokenId
    })

    if (options) {
      return options
    }
    return []
  }

  const paymentList = useMemo<Array<ITokenInfo>>(() => {
    if (!tokenList) return []
    if (showAll) return tokenList

    let currentChain = connectedAccount?.chain?.coinType || CoinType.ckb

    const showOptions = (): ITokenInfo[] => {
      let res: ITokenInfo[] = []
      if (currentChain !== undefined && currentChain !== null) {
        res = tokenList
          .filter((option: ITokenInfo) => {
            return (
              option.coin_type === currentChain &&
              ![CoinType.ckb, CoinType.doge].includes(
                option.coin_type as CoinType
              )
            )
          })
          .map((option: ITokenInfo) => {
            option.symbol = option.symbol?.toUpperCase()
            return option
          })
      }
      return res
    }

    return [...stripeOptions(), ...showOptions(), ...ckbWalletOptions()]
  }, [tokenList, showAll])

  useEffect(() => {
    if (!paymentList) return
    if (!selectedToken) {
      onTokenChange?.(paymentList[0])
      return
    }
  }, [paymentList, selectedToken])

  return (
    <div className="px-6">
      <ul className="py-3 bg-[#5262791a] rounded-xl border border-gray-300">
        {paymentList?.map((t) => (
          <li
            key={t.display_name}
            className=" px-4 py-3 flex items-center justify-between hover:bg-gray-200 cursor-pointer "
            onClick={() => {
              onTokenChange?.(t)
            }}
          >
            <span>
              <span className="flex items-center">
                <Iconfont className="mr-3" name={t.icon} size={36} />
                <span className=" flex flex-col justify-center">
                  <span className=" text-base font-medium leading-5 ">
                    {t.symbol}
                  </span>
                  <span className="text-xs font-normal text-gray-400">
                    {t.display_name}
                  </span>
                </span>
              </span>
            </span>
            <span
              className={cn(
                ' w-5 h-5 rounded-full flex items-center justify-center',
                {
                  'bg-primary': selectedToken?.token_id === t.token_id,
                  'bg-white border-2 border-gray-400 ':
                    selectedToken?.token_id != t.token_id
                }
              )}
            >
              {selectedToken?.token_id === t.token_id && (
                <Iconfont name="check" color="#FFFFFF" size="16" />
              )}
              {selectedToken?.token_id != t.token_id && <span></span>}
            </span>
          </li>
        ))}
      </ul>
      {selectedToken?.token_id === USD.tokenId && (
        <>
          <CheckBox
            checked={isAgreeFiatTerms}
            onChange={setIsAgreeFiatTerms}
            className="mt-4"
          >
            <span className=" text-sm font-medium flex items-center ">
              {tt('I have read and agreed to the')}
              <span
                className=" text-sky-700 hover:opacity-60 ml-1 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(fiatTermsLink)
                }}
              >
                {tt('Terms')}
              </span>
            </span>
          </CheckBox>
          <div className="my-4">
            <div className="flex justify-between items-center text-sm text-gray-500">
              <span>{tt('Stripe service fee')}</span>
              <span>{`$${thousandSplit(stripeServiceFee)}`}</span>
            </div>
            <div className="flex justify-between items-center text-xl font-bold mt-2">
              <span>{tt('Total')}</span>
              <span>{`$${thousandSplit(stripePaidAmount)}`}</span>
            </div>
          </div>
        </>
      )}
      {selectedToken?.token_id === DASBalanceTokenId ? (
        <>
          <a
            className="flex justify-between items-center px-3 my-3 bg-[#f2fff5] border-[#dcf6e2] border rounded-xl h-9 cursor-pointer"
            href={outSiteUrl?.dasBalanceUrl}
            target={isMobile() ? '_self' : '_blank'}
          >
            <span className=" text-sm font-normal">
              {tt('Deposit CKB to .bit Balance')}
            </span>
            <Iconfont name="arrow-right" color="#C4D0CD" size="16" />
          </a>
          {/*<a*/}
          {/*  className=" flex items-center my-3 px-3 cursor-pointer text-[#3e66b3] text-sm font-normal"*/}
          {/*  href={*/}
          {/*    lang?.value === LANGUAGE.zhCN*/}
          {/*      ? "https://talk.did.id/t/ckb-das-das-0-gas-ckb/284"*/}
          {/*      : "https://talk.did.id/t/how-do-i-register-das-account-with-ckb-0-gas-no-ckb-wallet-needed/285"*/}
          {/*  }*/}
          {/*  target="_blank"*/}
          {/*>*/}
          {/*  <Iconfont name="info" color="#3D66B3" size="14" />*/}
          {/*  <span className=" ml-1">*/}
          {/*    {tt("How to register with CKB (.bit Balance)?")}*/}
          {/*  </span>*/}
          {/*</a>*/}
        </>
      ) : null}
      {selectedToken?.token_id !== USD.tokenId && (
        <>
          <div className=" mt-6 mb-4 text-[32px] font-semibold  font-['Barlow-Medium'] flex justify-center ">
            <span>{thousandSplit(paidTokenAmount)}</span>
            <span className=" ml-1 text-neutral-400">
              {selectedToken?.symbol}
            </span>
          </div>
          <PaymentErrorTips selectedToken={selectedToken} />
        </>
      )}
      <DasButton
        className={cn('mb-9', {
          ['opacity-60 !cursor-not-allowed']: payDisabled
        })}
        loading={payLoading}
        isLoadingGradient={false}
        block
        black
        onClick={() => {
          if (payDisabled) return
          onPay?.()
        }}
      >
        {tt('Pay')}
      </DasButton>
    </div>
  )
}
